import * as React from "react";

import { Box, Divider, Typography } from "@material-ui/core";
import copy from "copy-to-clipboard";
import { useMutation } from "react-query";
import * as Redux from "react-redux";

import { MopButton } from "client/components";
import { OnboardingStatusContext, StatusContext } from "client/context";
import {
  Status,
  StatusLabel,
  StatusMap,
  StatusStep,
  useStyles,
} from "client/domain/OnboardingStatus";
import {
  useBranding,
  useMerchantModel,
  useRunOnce,
  useTranslation,
} from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { OnboardingStatusService } from "client/services";
import { errorActions } from "client/store";

export const determineCurrentState: (
  params: Pick<
    OnboardingStatusContext,
    | "inStoreDirectDebitAuthorized"
    | "inStoreTrainingComplete"
    | "onboardingStatus"
  >
) => Status = ({
  inStoreTrainingComplete,
  inStoreDirectDebitAuthorized,
  onboardingStatus,
}) => {
  if (onboardingStatus === "INELIGIBLE") {
    return "Declined";
  }

  if (onboardingStatus === "REQUIRES_ATTENTION") {
    return "Paused";
  }

  if (inStoreDirectDebitAuthorized) {
    return "Done";
  }

  // Direct debit is the next step right after InStore-Training
  // Thus, if it's complete, it means that all the other parameters for in-store
  // are true for this step to appear and be shown immediately after
  if (inStoreTrainingComplete) {
    return "InProgress";
  }

  return "None";
};

export const DirectDebitStep: React.FC = () => {
  const t = useTranslation();
  const { brandTitle } = useBranding();
  const dispatch = Redux.useDispatch();
  const classes = useStyles();
  const { merchant, setMerchant } = useMerchantModel();
  const { mutate: updateDirectDebitStep } = useMutation(
    OnboardingStatusService.updateInStoreFields,
    {
      onError: (err: Error) => {
        dispatch(errorActions.setError(err.message));
      },
      onSuccess: (restoreResponse) => {
        if (restoreResponse) {
          setMerchant(restoreResponse);
        }
      },
    }
  );

  const {
    inStoreDirectDebitAuthorized,
    inStoreTrainingComplete,
    onboardingStatus,
  } = React.useContext(StatusContext);

  const currentState = determineCurrentState({
    inStoreDirectDebitAuthorized,
    inStoreTrainingComplete,
    onboardingStatus,
  });

  const [copyClicked, setCopyClicked] = React.useState<boolean>(false);

  const statusMap: StatusMap = {
    None: {},
    InProgress: {
      className: classes.light,
    },
    Done: {
      className: classes.dark,
    },
    Paused: {},
    Reviewing: {
      className: classes.light,
    },
    RequiresAttention: {},
    Declined: {
      className: classes.light,
    },
  };

  const country = merchant.lead.registeredCountry.value;
  const companyIdCopy = `${brandTitle}’s company ID is ${t(
    "uiStatusCompanyIdNumber"
  )}`;
  const authorizationCodeCopy = `${brandTitle}’s Authorization Code is ${t(
    "uiStatusAuthorizationCodeNumber"
  )}`;

  const copyString = `I’d like to authorize the direct debit of ${brandTitle}’s in-store transaction fees.
    ${country === "New Zealand" ? authorizationCodeCopy : companyIdCopy}`;

  const { className } = statusMap[currentState];

  useRunOnce(() =>
    logAmplitudeEvent("STATUS_SCREEN", "direct_debit_card_loaded")
  );

  return (
    <StatusStep
      timeToFade={500}
      activeOnboardingStep="DirectDebit"
      stepStatus={currentState}
    >
      <Box className={`${className} ${classes.statusWrapper}`} mb={2} p={2}>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography variant="h6">
            <strong>{t("uiDirectDebitHeader")}</strong>
          </Typography>
          <StatusLabel status={currentState} />
        </Box>
        {currentState === "InProgress" && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            mt={1}
          >
            <Box display="flex" flexDirection="row" pl={0}>
              <Box flexGrow={2} width="40%">
                <Typography>{copyString}</Typography>
                <Typography className={classes.additionalParagraph}>
                  <strong>
                    {brandTitle}{" "}
                    {country === "New Zealand"
                      ? t("uiStatusAuthorizationCode")
                      : t("uiStatusCompanyId")}
                  </strong>
                </Typography>
              </Box>
              <Box display="flex" flexGrow={1} justifyContent="flex-end">
                <MopButton
                  mopVariant="primary"
                  onClick={() => {
                    logAmplitudeEvent("DIRECT_DEBIT_DONE", "direct_debit_done");
                    updateDirectDebitStep({
                      inStoreDirectDebitAuthorized: true,
                    });
                  }}
                >
                  {t("uiMarkAsDoneAction")}
                </MopButton>
              </Box>
            </Box>
            <Divider className={classes.divider} />
            <Box pl={0}>
              <Box alignItems="center" display="flex" flexDirection="row">
                <Box flexGrow={2} width="40%">
                  <Typography>
                    <strong>{t("uiDirectDebitEmailHeader")}</strong>
                  </Typography>
                  <Typography className={classes.additionalParagraph}>
                    {`${copyString}`}
                  </Typography>
                </Box>
                <Box flexGrow={1} display="flex" justifyContent="center">
                  <Box height="3rem" mr={0} width="5rem" />
                  <MopButton
                    className={classes.copyButton}
                    mopVariant="secondary"
                    onClick={() => {
                      copy(copyString);
                      setCopyClicked(true);
                      logAmplitudeEvent(
                        "STATUS_SCREEN",
                        "direct_debit_card_copy_text_to_email_clicked"
                      );
                    }}
                  >
                    {copyClicked
                      ? t("uiDirectDebitButtonPostClick")
                      : t("uiDirectDebitButtonPreClick")}
                  </MopButton>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </StatusStep>
  );
};
